/**
 * Compares two values in a `sort`-compatible way. null/undefined
 * compares larger than any non-null/undefined value.
 */
export function compareNullsLast(val1: any, val2: any) {
  if (val1 === undefined) val1 = null;
  if (val2 === undefined) val2 = null;

  if (val1 === null && val2 !== null) {
    return 1;
  }

  if (val2 === null && val1 !== null) {
    return -1;
  }

  if (typeof val1 === 'string' || typeof val2 === 'string') {
    return String(val1).localeCompare(String(val2), undefined, {
      sensitivity: 'base',
    });
  }

  return val1 > val2 ? 1 : val1 < val2 ? -1 : 0;
}

/**
 * Given two arrays of values, will compare them in a
 * `sort`-compatible way, with null/undefined sorting after any actual
 * value.
 *
 * Will first compare the first values in each array. If those are
 * equal, will compare the second two, and so on. If the end of either
 * array is reached, the shorter one sorts before the longer one.
 */
export function compareWithFallbacks<T>(
  arr1: Array<T | null>,
  arr2: Array<T | null>
) {
  const compare = arr1.reduce((acc, val, index) => {
    if (acc !== 0) return acc;

    if (arr2.length <= index) {
      return 1;
    }

    return compareNullsLast(val, arr2[index]);
  }, 0);

  if (compare === 0 && arr2.length > arr1.length) {
    return -1;
  }

  return compare;
}

/**
 * Given an array of items, sorts them using `compareWithFallbacks` by mapping each item to an
 * array of values using a provided callback. Uses a `Map` to memoize values for each item.
 *
 * @param items to sort
 * @param callback used to generate an array of values used to compare via compareWithFallbacks
 * @returns the sorted items
 */
export function sortArrayWithFallbacks<T>(items: T[], fn: (item: T) => any[]) {
  const itemValues = new Map();
  items.forEach(item => itemValues.set(item, fn(item)));

  return items
    .slice()
    .sort((a, b) => compareWithFallbacks(itemValues.get(a), itemValues.get(b)));
}
